/**
 * Normalize monetary input to a canonical "$XM", "$XB", "$XK" display.
 *
 * Accepts the many ways an M&A operator might type a deal value and collapses
 * them to a consistent format that reads well in the UI and sorts predictably.
 * Supports single values and ranges (split on "-"). Returns input unchanged
 * if it can't be parsed (e.g. "TBD", "N/A", "confidential").
 *
 * Examples:
 *   "40"             → "$40M"   (bare small number → assume millions in M&A context)
 *   "40M"            → "$40M"
 *   "$40m"           → "$40M"
 *   "40,000,000"     → "$40M"
 *   "40000000"       → "$40M"
 *   "1.5B"           → "$1.5B"
 *   "1,500,000,000"  → "$1.5B"
 *   "500K"           → "$500K"
 *   "500,000"        → "$500K"
 *   "$10M-$50M"      → "$10M-$50M"
 *   "10-50"          → "$10M-$50M"
 *   "10M - 50M"      → "$10M-$50M"
 *   "TBD"            → "TBD"    (unparseable, passes through)
 *   ""               → ""
 */
export function formatDealValue(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  const input = String(raw);
  if (!input.trim()) return input;

  // Ranges: split on "-" (not "–" which rarely appears), format each half.
  // Guard against lone "-" entries.
  if (input.includes("-")) {
    const parts = input.split("-").map(s => s.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      return `${formatDealValue(parts[0])}-${formatDealValue(parts[1])}`;
    }
  }

  // Strip currency symbols, commas, whitespace; uppercase for suffix matching.
  const cleaned = input.replace(/[$,\s]/g, "").toUpperCase();
  if (!cleaned) return input;

  let num: number;
  let suffix: "K" | "M" | "B";

  if (cleaned.endsWith("B")) {
    num = parseFloat(cleaned.slice(0, -1));
    suffix = "B";
  } else if (cleaned.endsWith("M")) {
    num = parseFloat(cleaned.slice(0, -1));
    suffix = "M";
  } else if (cleaned.endsWith("K")) {
    num = parseFloat(cleaned.slice(0, -1));
    suffix = "K";
  } else {
    num = parseFloat(cleaned);
    if (!Number.isFinite(num)) return input; // not a number → pass through
    // Disambiguate by magnitude. An M&A operator typing a bare "40" almost
    // always means $40M; typing "40000000" means the same thing in raw
    // dollars. Pick the suffix that gives the tidiest display.
    if (num >= 1_000_000_000) {
      num = num / 1_000_000_000;
      suffix = "B";
    } else if (num >= 1_000_000) {
      num = num / 1_000_000;
      suffix = "M";
    } else if (num >= 1_000) {
      num = num / 1_000;
      suffix = "K";
    } else {
      suffix = "M"; // small bare number → assume millions
    }
  }

  if (!Number.isFinite(num)) return input;

  // Drop trailing zeros after the decimal: 40.00 → "40", 1.50 → "1.5", 1.25 → "1.25"
  const formatted =
    num % 1 === 0
      ? num.toString()
      : num.toFixed(2).replace(/\.?0+$/, "");

  return `$${formatted}${suffix}`;
}

/**
 * Reverse of formatDealValue for decimal(15,2) columns.
 *
 * Takes loose user input and returns a raw-dollar numeric string suitable
 * for Postgres decimal storage (e.g. "$10M" → "10000000", "$1.5B" → "1500000000",
 * "40,000,000" → "40000000", bare "20" → "20000000" in M&A millions context).
 *
 * Returns null if the input is empty, whitespace-only, or can't be parsed as
 * a number — caller should skip the save in that case rather than sending
 * garbage to a decimal column which would reject it at the DB level anyway.
 *
 * Used by CapitalProviderDetail for minDealSize/maxDealSize/minEbitda, which
 * are decimal columns on capital_provider_profiles (unlike the companies
 * table's financial fields which are stored as text and can hold the
 * formatted "$10M" string directly).
 */
export function parseDealValueToDollars(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const input = String(raw).trim();
  if (!input) return null;

  const cleaned = input.replace(/[$,\s]/g, "").toUpperCase();
  if (!cleaned) return null;

  let multiplier = 1;
  let numericPart = cleaned;

  if (cleaned.endsWith("B")) {
    multiplier = 1_000_000_000;
    numericPart = cleaned.slice(0, -1);
  } else if (cleaned.endsWith("M")) {
    multiplier = 1_000_000;
    numericPart = cleaned.slice(0, -1);
  } else if (cleaned.endsWith("K")) {
    multiplier = 1_000;
    numericPart = cleaned.slice(0, -1);
  } else {
    // No suffix. Disambiguate by magnitude — matches formatDealValue's logic
    // so the round-trip is stable for typical M&A inputs.
    const asNumber = parseFloat(numericPart);
    if (!Number.isFinite(asNumber)) return null;
    if (asNumber >= 1_000) {
      // "40000000" or "40,000,000" → already in raw dollars
      multiplier = 1;
    } else {
      // Bare small number → assume millions (matches formatDealValue)
      multiplier = 1_000_000;
    }
  }

  const n = parseFloat(numericPart);
  if (!Number.isFinite(n)) return null;

  // Result is always an integer count of dollars. Decimal(15,2) can hold it.
  const dollars = Math.round(n * multiplier);
  return dollars.toString();
}
